import { useQuery } from "@tanstack/react-query";
import { queueApi } from "@bw/shared/api";
import type { CheckedInPlayerResponse } from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";

/**
 * A party member's "recent + search" invite-candidates picker (party-
 * persistence + invite-picker rework, Task 6).
 *
 * GET /queue/party/{partyId}/invite-candidates — checked-in players at the
 * party's facility, already excluding current members / pending invitees / the
 * caller (server-side, Task 5). `q` is ALWAYS forwarded (blank included — the
 * blank/no-query call is the "recently active" default list, recency-desc);
 * a non-blank `q` switches to a server-side name search. `limit` caps the
 * result (server clamps 1..50). No client-side filter/sort/cap — thin
 * presentation, mirrors `usePartnerCandidates`.
 *
 * Keyed by (partyId, trimmed q) so each distinct search has its own cache slot.
 * `retry: false` so a failure (e.g. the party isn't open, 409) surfaces
 * immediately as an honest inline error rather than retrying silently.
 */
export function useInviteCandidates(partyId: string, q: string, limit: number) {
  const trimmedQ = (q ?? "").trim();
  return useQuery<CheckedInPlayerResponse[]>({
    queryKey: queryKeys.party.inviteCandidates(partyId, trimmedQ),
    queryFn: () =>
      queueApi.getInviteCandidates(partyId, { q: trimmedQ, limit }),
    enabled: Boolean(partyId),
    retry: false,
  });
}
